import type { BlockCardsWithOptionalIcon } from 'types/block-cards-with-optional-icon';

import React from 'react';

import { Wrap } from '@leafwell/components';

import { getRandomInt } from 'lib/utils/generateRandomInt';

import HeadingBody from './heading-body';
import Figure from './figure';

const Card: React.FC<
  BlockCardsWithOptionalIcon['cards'][0] & {
    index?: number;
    isColored?: boolean;
  }
> = ({ cardTitle, cardDescription, cardIcon, index, isColored }) => {
  const colorArray = [
    'bg-[#FFE194]',
    'bg-lilac-300',
    'bg-[#ADEFD1]',
    'bg-[#FFCCC6]',
  ];
  return (
    <li
      className={[
        'grid gap-y-8 px-8 py-10 rounded-2xl lg:w-108',
        isColored ? colorArray[index] : 'bg-white border border-[#DEE4E8]',
      ].join(' ')}
    >
      {cardIcon ? <Figure {...cardIcon} /> : null}
      <div className="flex flex-col gap-y-3">
        {cardTitle ? (
          <HeadingBody
            level="5"
            tagName="p"
            title={cardTitle}
            className="font-semibold"
          />
        ) : null}
        {cardDescription ? (
          <HeadingBody
            level="6"
            tagName="p"
            title={cardDescription}
            className="text-gray-700"
          />
        ) : null}
      </div>
    </li>
  );
};

const CardsWithOptionalIcon: React.FC<BlockCardsWithOptionalIcon> = ({
  sectionId,
  title,
  description,
  backgroundColor,
  cards,
}) => {
  const isColored = cards?.length < 5 && backgroundColor === 'none';
  return (
    <section
      id={sectionId}
      className={[
        backgroundColor === 'green'
          ? 'bg-dark-green-gradient my-12 md:my-16'
          : 'bg-white',
      ].join(' ')}
    >
      <Wrap>
        {title || description ? (
          <div className="grid gap-y-6 justify-items-center">
            {title && (
              <HeadingBody
                level="2"
                tagName="h2"
                title={title}
                className={[
                  'font-semibold text-center',
                  backgroundColor === 'green' ? 'text-white' : 'text-black',
                ].join(' ')}
              />
            )}
            {description && (
              <HeadingBody
                level="6"
                tagName="p"
                title={description}
                className={[
                  'text-center lg:max-w-7xl',
                  backgroundColor === 'green' ? 'text-white' : 'text-gray-700',
                ].join(' ')}
              />
            )}
          </div>
        ) : null}
        {cards?.length > 0 ? (
          <ul className="flex flex-wrap justify-between gap-y-6">
            {cards?.map(({ cardTitle, cardDescription, cardIcon }, index) => {
              const idx = getRandomInt(1, 1000000);
              return (
                <Card
                  key={`${idx}-optional-icon-card`}
                  cardTitle={cardTitle}
                  cardDescription={cardDescription}
                  cardIcon={cardIcon}
                  index={index}
                  isColored={isColored}
                />
              );
            })}
          </ul>
        ) : null}
      </Wrap>
    </section>
  );
};

export default CardsWithOptionalIcon;
